import React, { useState } from 'react';
import FileUpload from '../../components/FileUpload';
import LinearProgress from '@mui/material/LinearProgress';
import { ChevronDown, ChevronUp } from 'lucide-react';
import axios from '../../axiosConfig';
import { useUser } from 'context/userContext';

const apiUrl = process.env.REACT_APP_API_URL
type Step2UploadProps = {
  onComplete: () => void;
};

const Step2Upload: React.FC<Step2UploadProps> = ({ onComplete }) => {
  const [file, setFile] = useState<File | null>(null);
  const [status, setStatus] = useState<'idle' | 'processing' | 'success' | 'error'>('idle');
  const { email } = useUser();

  const [fileName, setFileName] = useState<string | null>(null);
  const [showLog, setShowLog] = useState<boolean>(false);
  const [processingLog, setProcessingLog] = useState<string>('');
  const [errorMessage, setErrorMessage] = useState<string>('');
  const [isDownloading, setIsDownloading] = useState(false);



const processExcelFile = async (file: File) => {

  setStatus('processing');

  setErrorMessage('');

  const useAzureFunction = process.env.NODE_ENV === "production" ? "true" : "false";

  const formData = new FormData();
  formData.append('File', file);
  formData.append('UseAzureFunction', useAzureFunction);
  formData.append('UserName', email);
  formData.append('UserEmail', email);


  let serverProgress = 15;
  let progressIncrement = 0.5;
  let timeElapsed = 0;
  let progressIntervalId: NodeJS.Timeout | null = null;

  try {
    // Animación de progreso simulada
    progressIntervalId = setInterval(() => {
      timeElapsed += 250;

      if (timeElapsed > 30000) progressIncrement = 1.5;
      else if (timeElapsed > 20000) progressIncrement = 1.0;
      else if (timeElapsed > 10000) progressIncrement = 0.7;

      if (serverProgress < 95) {
        serverProgress += progressIncrement;
       
      } else if (progressIntervalId) {
        clearInterval(progressIntervalId);
      }
    }, 250);

    // Solicitud con axios y seguimiento de subida
    const response = await axios.post(`${apiUrl}/excel/pre-process`, formData, {
      headers: {
        'Content-Type': 'multipart/form-data',
      },
      onUploadProgress: (progressEvent) => {
        if (progressEvent.total) {
          const uploadProgress = Math.round((progressEvent.loaded / progressEvent.total) * 15);
         
        }
      }
    });

    if (progressIntervalId) clearInterval(progressIntervalId);

    const result = response.data;

    if (result.success) {
     
      setStatus('success');
      setFileName(result.fileName);
      setProcessingLog(result.processingLog ?? 'No log received.');
    } else {
      setStatus('error');
      setErrorMessage(result.message || 'The process failed on the server.');
      setProcessingLog(result.processingLog || result.message || 'An error occurred.');
    }
  } catch (error: any) {
    if (progressIntervalId) clearInterval(progressIntervalId);

    const message = error.response?.data?.message || error.message || 'Unexpected error.';
    setStatus('error');
    setErrorMessage(`Error: ${message}`);
    setProcessingLog(JSON.stringify(error.response?.data ?? {}, null, 2));
  }
};
const downloadProcessedFile = async () => {
  if (!fileName) {
    setErrorMessage('Invalid file name.');
    return;
  }

  try {
    setIsDownloading(true);

    const response = await axios.get(`${apiUrl}/excel/download/${fileName}`, {
      responseType: 'blob',
    });

    const url = window.URL.createObjectURL(new Blob([response.data]));
    const link = document.createElement('a');
    link.href = url;
    link.setAttribute('download', fileName);
    document.body.appendChild(link);
    link.click();
    link.remove();
  } catch (error: any) {
    setErrorMessage(`Error downloading file: ${error.message}`);
  } finally {
    setIsDownloading(false);
  }
};



  const handleFileAccepted = (acceptedFile: File) => {
    setFile(acceptedFile);
    processExcelFile(acceptedFile);
  };

  const handleDownload = () => {
    
       downloadProcessedFile();
 
  };

  const handleContinue = () => {
    onComplete();
  };

  const toggleLog = () => {
    setShowLog(!showLog);
  };

  return (
    <div className="w-full">
      <h2 className="text-xl font-semibold mb-4">Upload Your Filled Template</h2>
      <p className="mb-6 text-gray-600">
        Upload the Excel file that you've filled with your data. 
        The system will validate your input and provide feedback on any issues found.
      </p>

      {!file && (
        <FileUpload 
          onFileAccepted={handleFileAccepted} 
          fileType=".xlsx"
          label="Upload Your Filled Template"
        />
      )}

      {file && (
        <div className="mb-4 p-3 bg-gray-50 border border-gray-200 rounded-lg flex items-center">
          <div className="bg-blue-100 p-2 rounded-full mr-3">
            <svg className="w-5 h-5 text-blue-500" fill="none" stroke="currentColor" viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
            </svg>
          </div>
          <div className="flex-1 min-w-0">
            <p className="text-sm font-medium text-gray-900 truncate">
              {file.name}
            </p>
            <p className="text-xs text-gray-500">
              {(file.size / 1024).toFixed(2)} KB
            </p>
          </div>
        </div>
      )}

      {status === 'processing' && (
        <div className="mt-6">
          <LinearProgress />
        </div>
      )}

      {status === 'success' && (
        <div className="mt-6">
          <div className="p-4 bg-green-50 border border-green-200 rounded-lg mb-4">
            <div className="flex">
              <svg className="w-5 h-5 text-green-500 mt-0.5" fill="none" stroke="currentColor" viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
              </svg>
              <h3 className="ml-2 font-medium text-green-800">Processing successful!</h3>
            </div>
            <p className="mt-2 text-sm text-green-700">
              Your file has been successfully processed. You can view the processing log, download the processed file, or continue to the next step.
            </p>
          </div>
          
          {/* Processing Log Accordion */}
          <div className="mb-6 border border-gray-200 rounded-lg overflow-hidden">
            <button
              onClick={toggleLog}
              className="w-full p-4 flex justify-between items-center bg-gray-50 hover:bg-gray-100 transition-colors duration-200"
            >
              <span className="font-medium text-gray-800">Processing Log</span>
              {showLog ? 
                <ChevronUp className="w-5 h-5 text-gray-600" /> : 
                <ChevronDown className="w-5 h-5 text-gray-600" />
              }
            </button>
            
            {showLog && (
              <div className="p-4 bg-black">
                <div className="max-h-80 overflow-y-auto">
                  <pre className="text-green-400 text-xs font-mono whitespace-pre">{processingLog}</pre>
                </div>
              </div>
            )}
          </div>
          
          <div className="flex justify-between">
            <button
              onClick={handleDownload}
              disabled={!fileName}
              className="px-5 py-2 bg-green-500 hover:bg-green-600 text-white font-medium rounded-lg transition-colors duration-200 flex items-center"
            >
              <svg className="w-5 h-5 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 16v1a3 3 0 003 3h10a3 3 0 003-3v-1m-4-4l-4 4m0 0l-4-4m4 4V4" />
              </svg>
              {isDownloading ? (
                <span className="text-sm">Downloading...</span>
              ) : (
                <>
                  <svg className="w-5 h-5 mr-2" />
                  Download Processed File
                </>
              )}
            </button>
            
            <button
              onClick={handleContinue}
              disabled={true}
              className="px-5 py-2 bg-blue-500 hover:bg-blue-600 text-white font-medium rounded-lg transition-colors duration-200"
            >
              Continue to Next Step
            </button>
          </div>
        </div>
      )}

      {status === 'error' && (
        <div className="mt-6">
          <div className="p-4 bg-red-50 border border-red-200 rounded-lg mb-4">
            <div className="flex">
              <svg className="w-5 h-5 text-red-500 mt-0.5" fill="none" stroke="currentColor" viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4m0 4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
              </svg>
              <h3 className="ml-2 font-medium text-red-800">Processing Error</h3>
            </div>
            <p className="mt-2 text-sm text-red-700">
              {errorMessage}
            </p>
          </div>
          
          {/* Error Log Accordion */}
          <div className="mb-6 border border-gray-200 rounded-lg overflow-hidden">
            <button
              onClick={toggleLog}
              className="w-full p-4 flex justify-between items-center bg-gray-50 hover:bg-gray-100 transition-colors duration-200"
            >
              <span className="font-medium text-gray-800">Error Details</span>
              {showLog ? 
                <ChevronUp className="w-5 h-5 text-gray-600" /> : 
                <ChevronDown className="w-5 h-5 text-gray-600" />
              }
            </button>
            
            {showLog && (
              <div className="p-4 bg-black">
                <div className="max-h-80 overflow-y-auto">
                  <pre className="text-red-400 text-xs font-mono whitespace-pre">{processingLog}</pre>
                </div>
              </div>
            )}
          </div>
          
          {/* Option to try again */}
          <div className="flex justify-center">
            <button
              onClick={() => setFile(null)}
              className="px-5 py-2 bg-gray-500 hover:bg-gray-600 text-white font-medium rounded-lg transition-colors duration-200"
            >
              Try with another file
            </button>
          </div>
        </div>
      )}
    </div>
  );
};

export default Step2Upload;
